import { HTMLAttributeAnchorTarget, MouseEventHandler } from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import loadingAnime from '../../../../public/animations/loading.json';
import isAbsoluteUrl from '../../../core/helpers/isAbsoluteUrl';
import Link from '../Link/Link';

const Lottie = dynamic(() => import('../Lottie/Lottie'));

type Props = {
  id?: string;
  children: React.ReactNode;
  className?: string;
  to?: string;
  type?: 'button' | 'submit' | 'reset' | undefined;
  size?: 'xs' | 'small' | 'medium' | 'large';
  color?: 'white' | 'red' | 'green' | 'black' | 'light-red' | 'transparent';
  textColor?: 'white' | 'black' | 'red' | 'blue' | 'gray';
  fontWeight?: 'thin' | 'extralight' | 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
  buttonStyle?: 'button' | 'link';
  target?: HTMLAttributeAnchorTarget;
  rel?:
    | 'alternate'
    | 'author'
    | 'bookmark'
    | 'external'
    | 'help'
    | 'license'
    | 'next'
    | 'nofollow'
    | 'noopener'
    | 'noreferrer'
    | 'prev'
    | 'search'
    | 'tag';
  disabled?: boolean;
  rounded?: boolean;
  loading?: boolean;
  loaderWidth?: number;
  border?: boolean;
  borderColor?: 'red' | 'gray';
  onClick?: MouseEventHandler<Element>;
  customeDisabledStyle?: boolean;
};

const Button = ({
  id,
  className,
  children,
  to,
  size = 'medium',
  color = 'black',
  textColor = 'white',
  fontWeight = 'bold',
  target,
  type = 'button',
  rel = 'noreferrer',
  buttonStyle = 'button',
  rounded = false,
  disabled = false,
  loading = false,
  loaderWidth = 190,
  border,
  borderColor = 'red',
  onClick = () => {},
  customeDisabledStyle = false,
}: Props) => {
  const buttonClassName = classNames(
    'flex justify-center items-center hover:opacity-60',
    className,
    {
      'link-small': buttonStyle === 'link' && (size === 'xs' || size === 'small'),
      'link-medium': buttonStyle === 'link' && (size === 'medium' || size === 'large'),
      'btn-xs': buttonStyle === 'button' && size === 'xs',
      'btn-small': buttonStyle === 'button' && size === 'small',
      'btn-medium': buttonStyle === 'button' && size === 'medium',
      'btn-large': buttonStyle === 'button' && size === 'large',
      'bg-white': color === 'white',
      'bg-teal-600': color === 'green',
      'bg-orange-700': color === 'red',
      'bg-black': color === 'black',
      'bg-red-100': color === 'light-red',
      'bg-transparent': color === 'transparent',
      'text-white': textColor === 'white',
      'text-orange-700': textColor === 'red',
      'text-teal-600': textColor === 'blue',
      'text-gray-600': textColor === 'gray',
      'text-black': textColor === 'black',
      'opacity-30': disabled && !customeDisabledStyle,
      'rounded-4xl': rounded,
      rounded: !rounded,
      'pointer-events-none': disabled,
      'pointer-events-auto': !disabled,
      'border ': border,
      'border-orange-700': borderColor === 'red',
      'border-gray-200': borderColor === 'gray',
      'font-thin': fontWeight === 'thin',
      'font-extralight': fontWeight === 'extralight',
      'font-light': fontWeight === 'light',
      'font-normal': fontWeight === 'normal',
      'font-medium': fontWeight === 'medium',
      'font-semibold': fontWeight === 'semibold',
      'font-bold': fontWeight === 'bold',
    }
  );

  const events = {
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      // event.button === 0 = "left click"
      // event.metaKey = "open link in new tab" (mac)
      // event.ctrlKey = "open link in new tab" (windows/linux)
      // event.shiftKey = "open link in new window"
      if (onClick && (event.button === 0 || event.metaKey || event.ctrlKey || event.shiftKey)) {
        onClick(event);
      }
    },
  };

  const button = (
    <button
      data-testid="button"
      id={id}
      disabled={disabled}
      // eslint-disable-next-line react/button-has-type
      type={type}
      className={buttonClassName}
      onClick={onClick}
    >
      {loading ? (
        <Lottie
          style={{ width: loaderWidth, height: 90 }}
          params={{
            animationData: loadingAnime,
          }}
        />
      ) : (
        children
      )}
    </button>
  );

  if (disabled || !to) return button;

  return (
    <Link href={to} passHref prefetch={false}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <a
        className={buttonClassName}
        target={target || (isAbsoluteUrl(to) ? '_blank' : '_self')}
        rel={rel}
        onClick={events.onClick}
      >
        {children}
      </a>
    </Link>
  );
};

export default Button;
